import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, EnvelopeSimple, Buildings, Phone, User, Star, PaperPlaneTilt, Key } from "@phosphor-icons/react";
import { toast } from "sonner";

/** Admin onboards an existing customer by creating a client account.
 *  Calls onCreated(newClient) on success. */
export function CreateClientModal({ open, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [delivery, setDelivery] = useState("setup_link");
  const [activatePlan, setActivatePlan] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const reset = () => {
    setName(""); setEmail(""); setCompanyName(""); setPhone(""); setNote("");
    setDelivery("setup_link"); setActivatePlan(false);
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (!email.trim()) { toast.error("Email is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/clients", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        company_name: companyName.trim() || null,
        phone: phone.trim() || null,
        delivery_method: delivery,
        activate_maintenance: activatePlan,
        note: note.trim() || null,
      });
      const msg = delivery === "setup_link"
        ? `Setup link emailed to ${data.email}`
        : `Temp password emailed to ${data.email}`;
      toast.success(msg + (activatePlan ? " · maintenance plan activated" : ""));
      onCreated?.(data);
      reset();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create client");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 overflow-y-auto" data-testid="create-client-modal">
      <div className="border border-[#FF2D2D] bg-[#0a0a0a] w-full max-w-lg p-6 my-8">
        <div className="flex justify-between items-start mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF2D2D]">Onboard a client</div>
            <h3 className="font-display text-2xl mt-1">Add existing customer</h3>
            <div className="text-xs text-[#a3a3a3] mt-1">For legacy maintenance customers, you'll send them a portal access email below.</div>
          </div>
          <button onClick={onClose} className="text-[#a3a3a3] hover:text-white" aria-label="Close"><X size={18} /></button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2 flex items-center gap-1.5"><User size={11} /> Full name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="cc-name"
              placeholder="Jane Smith"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 focus:border-[#FF2D2D] outline-none text-sm"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2 flex items-center gap-1.5"><EnvelopeSimple size={11} /> Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="cc-email"
              placeholder="jane@business.com"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 focus:border-[#FF2D2D] outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2 flex items-center gap-1.5"><Buildings size={11} /> Company <span className="text-[#666] normal-case tracking-normal">(optional)</span></label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              data-testid="cc-company"
              placeholder="Acme Co."
              className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 focus:border-[#FF2D2D] outline-none text-sm"
            />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2 flex items-center gap-1.5"><Phone size={11} /> Phone <span className="text-[#666] normal-case tracking-normal">(optional)</span></label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              data-testid="cc-phone"
              placeholder="+1 555 123 4567"
              className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 focus:border-[#FF2D2D] outline-none text-sm"
            />
          </div>
        </div>

        <label className="block text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mt-4 mb-2">Internal note <span className="text-[#666] normal-case tracking-normal">(optional, only you see this)</span></label>
        <textarea
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid="cc-note"
          placeholder="e.g. Existing 2024 maintenance contract — site hosted on Cloudflare"
          className="w-full bg-[#050505] border border-[#262626] px-4 py-2.5 text-sm focus:border-[#FF2D2D] outline-none"
        />

        {/* Delivery method */}
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#a3a3a3] mb-2">How should they sign in first?</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className={`flex items-start gap-2 p-3 border cursor-pointer ${delivery === "setup_link" ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626]"}`}>
              <input
                type="radio"
                name="delivery"
                value="setup_link"
                checked={delivery === "setup_link"}
                onChange={() => setDelivery("setup_link")}
                data-testid="cc-delivery-setup"
                className="mt-1"
              />
              <div>
                <div className="text-sm font-semibold flex items-center gap-1.5"><PaperPlaneTilt size={12} /> Setup link <span className="text-[10px] text-[#a3a3a3] normal-case font-normal">(recommended)</span></div>
                <div className="text-xs text-[#a3a3a3] mt-1">They click a one-time link, choose their own password. Expires in 14 days.</div>
              </div>
            </label>
            <label className={`flex items-start gap-2 p-3 border cursor-pointer ${delivery === "temp_password" ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626]"}`}>
              <input
                type="radio"
                name="delivery"
                value="temp_password"
                checked={delivery === "temp_password"}
                onChange={() => setDelivery("temp_password")}
                data-testid="cc-delivery-temp"
                className="mt-1"
              />
              <div>
                <div className="text-sm font-semibold flex items-center gap-1.5"><Key size={12} /> Temp password</div>
                <div className="text-xs text-[#a3a3a3] mt-1">We auto-generate one and email it. They can change it from Account.</div>
              </div>
            </label>
          </div>
        </div>

        {/* Maintenance toggle */}
        <label className={`mt-4 flex items-start gap-3 p-3 border cursor-pointer ${activatePlan ? "border-[#FF2D2D] bg-[#FF2D2D]/5" : "border-[#262626]"}`}>
          <input
            type="checkbox"
            checked={activatePlan}
            onChange={(e) => setActivatePlan(e.target.checked)}
            data-testid="cc-activate-maintenance"
            className="mt-1"
          />
          <div className="flex-1">
            <div className="text-sm font-semibold flex items-center gap-1.5"><Star size={12} weight={activatePlan ? "fill" : "regular"} className={activatePlan ? "text-[#FF2D2D]" : ""} /> Activate 1-year Maintenance Plan now</div>
            <div className="text-xs text-[#a3a3a3] mt-1">Free website updates for the next 365 days. Renewal mode defaults to "remind".</div>
          </div>
        </label>

        <div className="flex gap-3 mt-6">
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="cc-submit"
            className="btn-primary flex-1 px-4 py-3 text-sm font-bold uppercase tracking-wider disabled:opacity-50"
          >
            {submitting ? "Creating…" : "Create & send invite →"}
          </button>
          <button onClick={onClose} className="btn-outline flex-1 px-4 py-3 text-sm uppercase tracking-wider">Cancel</button>
        </div>
      </div>
    </div>
  );
}
